import React, { useState, useEffect } from 'react';
import { knowledgeBase, agents } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { BookOpen, Search, Upload, FileText, CheckCircle, Clock, XCircle, Trash2, Bot, FileCheck, Sparkles, AlertCircle } from 'lucide-react';
import AnimatedDropdown from './AnimatedDropdown';

const KnowledgeBaseManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentsList, setAgentsList] = useState([]);
  const [queryText, setQueryText] = useState('');
  const [queryResults, setQueryResults] = useState([]);
  const [querying, setQuerying] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');
  const [deleteModal, setDeleteModal] = useState({ show: false, docId: null });
  const [purgeModal, setPurgeModal] = useState({ show: false, agentId: '', confirmText: '' });
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    loadStats();
    loadAgents();
  }, []);

  // Load documents after agents are loaded to get agent names
  useEffect(() => {
    if (agentsList.length > 0) {
      loadDocuments();
    }
  }, [agentsList]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await knowledgeBase.listDocuments();
      const documents = response.data;
      
      // Enrich documents with agent names
      const enrichedDocuments = documents.map(doc => {
        if (!doc.agent_id) {
          return {
            ...doc,
            agent_name: 'No Agent'
          };
        }
        const agent = agentsList.find(a => a.id === doc.agent_id);
        return {
          ...doc,
          agent_name: agent ? agent.name : 'Unknown Agent'
        };
      });
      
      setDocuments(enrichedDocuments);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await knowledgeBase.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await agents.list();
      setAgentsList(response.data || []);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!selectedAgent) {
      alert('Please select an agent for this document');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (uploadTitle) formData.append('title', uploadTitle);
      formData.append('agent_id', selectedAgent); // Always required now

      const response = await knowledgeBase.uploadDocument(formData);

      // Add the new document with agent name
      const newDoc = {
        ...response.data,
        agent_name: agentsList.find(a => a.id === selectedAgent)?.name || 'Unknown Agent'
      };
      setDocuments([newDoc, ...documents]);
      setSelectedFile(null);
      setUploadTitle('');
      setSelectedAgent('');
      loadStats();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    setDeleteModal({ show: true, docId });
  };

  const confirmDelete = async () => {
    if (!deleteModal.docId) return;

    try {
      const response = await knowledgeBase.deleteDocument(deleteModal.docId);
      setDocuments(documents.filter(doc => doc.id !== deleteModal.docId));
      loadStats();
      setDeleteModal({ show: false, docId: null });
      // The document may predate doc_id-linked vector IDs, in which case its
      // content is still in Pinecone and still reachable by the agent.
      if (response.data?.warning) {
        alert(response.data.warning);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document: ' + (error.response?.data?.detail || error.message));
      setDeleteModal({ show: false, docId: null });
    }
  };


  const handleQuery = async (e) => {
    e.preventDefault();
    if (!queryText.trim()) return;

    setQuerying(true);
    try {
      const response = await knowledgeBase.query({
        query: queryText,
        agent_id: selectedAgent || null,
        top_k: 5,
        score_threshold: 0.3
      });
      setQueryResults(response.data.results || []);
    } catch (error) {
      console.error('Error querying knowledge base:', error);
      alert('Error querying knowledge base: ' + (error.response?.data?.detail || error.message));
    } finally {
      setQuerying(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'processing': return 'text-warning';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const handlePurgeAll = () => {
    setPurgeModal({ show: true, agentId: selectedAgent || '', confirmText: '' });
  };

  const confirmPurge = async () => {
    if (purgeModal.confirmText !== 'DELETE' || !purgeModal.agentId) return;

    setPurging(true);
    try {
      const payload = { confirm: 'DELETE' };
      // '__org__' means the org-wide documents that were uploaded without an
      // agent. The backend never wipes every agent at once.
      if (purgeModal.agentId !== '__org__') {
        payload.agent_id = purgeModal.agentId;
      }

      const response = await knowledgeBase.purge(payload);
      setPurgeModal({ show: false, agentId: '', confirmText: '' });
      await loadDocuments();
      loadStats();
      alert(
        `${response.data.message}.\n\n` +
        `${response.data.documents_deleted} document record(s) removed from ` +
        `namespace ${response.data.namespace}.\n\n${response.data.note}`
      );
    } catch (error) {
      console.error('Error clearing knowledge base:', error);
      alert('Error clearing knowledge base: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurging(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'processing': return Clock;
      case 'failed': return XCircle;
      default: return FileText;
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
            <BookOpen className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Knowledge Base</h1>
            <p className="text-muted-foreground">Manage documents and search your knowledge base</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'documents'
              ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/30'
          }`}
        >
          <FileText className="w-4 h-4" />
          Documents
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'search'
              ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/30'
          }`}
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-lg p-4 shadow-elegant">
          <div className="flex items-center gap-2 mb-1">
            <FileCheck className="w-4 h-4 text-primary" />
            <div className="text-xl font-bold text-foreground">{stats.total_documents || 0}</div>
          </div>
          <div className="text-sm text-muted-foreground">Total Documents</div>
        </div>
        <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-lg p-4 shadow-elegant">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="text-xl font-bold text-foreground">{stats.total_chunks || 0}</div>
          </div>
          <div className="text-sm text-muted-foreground">Total Chunks</div>
        </div>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 min-h-0 overflow-hidden">
          {/* Left Column - Upload Form */}
          <div className="flex flex-col min-h-0 overflow-y-auto animate-slide-in-left"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 380px)'
            }}
          >
            <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <Upload className="w-5 h-5 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Upload Document</h3>
              </div>
              <form onSubmit={handleFileUpload} className="space-y-4 flex-1 flex flex-col">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      File (PDF, TXT, MD)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.txt,.md"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/20 file:text-primary hover:file:bg-primary/30 cursor-pointer"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Title (optional)
                    </label>
                    <input
                      type="text"
                      value={uploadTitle}
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder="Document title"
                      className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Agent <span className="text-destructive">*</span>
                  </label>
                  <AnimatedDropdown
                    value={selectedAgent}
                    onChange={(value) => setSelectedAgent(value)}
                    options={[
                      { value: '', label: 'Select an Agent' },
                      ...agentsList.map(agent => ({ value: agent.id, label: agent.name }))
                    ]}
                    placeholder="Select an Agent"
                    icon={Bot}
                  />
                </div>
                <button
                  type="submit"
                  disabled={uploading || !selectedFile}
                  className="w-full mt-auto px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Document
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Right Column - Documents List */}
          <div className="flex flex-col min-h-0 overflow-hidden animate-slide-in-right animate-delay-100">
            <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col overflow-y-auto"
              style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 380px)'
              }}
            >
              <div className="flex items-center justify-between gap-3 mb-6 sticky top-0 bg-card/95 backdrop-blur-md pb-4 border-b border-border/50">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents
                </h3>
                <button
                  onClick={handlePurgeAll}
                  title="Delete every document and clear the agent's Pinecone namespace"
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 transition-all duration-300"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete All
                </button>
              </div>
              {loading ? (
                <div className="text-center py-12">
                  <LoadingSpinner size="lg" text="Loading documents..." />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-4 flex-1">
                  {documents.map(doc => {
                    const StatusIcon = getStatusIcon(doc.status);
                    return (
                      <div key={doc.id} className="bg-background/30 border border-border/60 rounded-lg p-4 hover:bg-background/50 transition-all duration-300">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                              doc.status === 'completed' ? 'bg-success/20' :
                              doc.status === 'processing' ? 'bg-warning/20' :
                              doc.status === 'failed' ? 'bg-destructive/20' :
                              'bg-muted/30'
                            }`}>
                              <StatusIcon className={`w-5 h-5 ${
                                doc.status === 'completed' ? 'text-success' :
                                doc.status === 'processing' ? 'text-warning' :
                                doc.status === 'failed' ? 'text-destructive' :
                                'text-muted-foreground'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-foreground font-medium mb-1 truncate">{doc.title || doc.filename}</h4>
                              <p className="text-muted-foreground text-sm mb-2">
                                {doc.filename} • {formatFileSize(doc.file_size)} • {doc.chunks_count} chunks
                              </p>
                              <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <Bot className="w-3 h-3 text-primary" />
                                  <span className="text-sm text-primary">{doc.agent_name}</span>
                                </div>
                                <span className={`text-sm font-medium ${getStatusColor(doc.status)}`}>
                                  {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                </span>
                                {doc.error_message && (
                                  <span className="text-xs text-destructive flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {doc.error_message}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/20 hover:bg-destructive/30 text-destructive flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-destructive/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 min-h-0 overflow-hidden">
          {/* Left Column - Search Form */}
          <div className="flex flex-col min-h-0 overflow-y-auto animate-slide-in-left"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 380px)'
            }}
          >
            <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <Search className="w-5 h-5 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Search Knowledge Base</h3>
              </div>
              <form onSubmit={handleQuery} className="space-y-4 flex-1 flex flex-col">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    Query
                  </label>
                  <input
                    type="text"
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    placeholder="What would you like to know?"
                    className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Agent <span className="text-destructive">*</span>
                  </label>
                  <AnimatedDropdown
                    value={selectedAgent}
                    onChange={(value) => setSelectedAgent(value)}
                    options={[
                      { value: '', label: 'Select an Agent' },
                      ...agentsList.map(agent => ({ value: agent.id, label: agent.name }))
                    ]}
                    placeholder="Select an Agent"
                    icon={Bot}
                  />
                </div>
                <button
                  type="submit"
                  disabled={querying || !queryText.trim()}
                  className="w-full mt-auto px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient"
                >
                  {querying ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Search
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Right Column - Search Results */}
          <div className="flex flex-col min-h-0 overflow-hidden animate-slide-in-right animate-delay-100">
            {queryResults.length > 0 ? (
              <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col overflow-y-auto"
                style={{ 
                  scrollBehavior: 'smooth',
                  maxHeight: 'calc(100vh - 380px)'
                }}
              >
                <div className="flex items-center gap-2 mb-6 sticky top-0 bg-card/95 backdrop-blur-md pb-4 border-b border-border/50">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <h3 className="text-xl font-semibold text-foreground">
                    Search Results ({queryResults.length})
                  </h3>
                </div>
                <div className="space-y-4 flex-1">
                  {queryResults.map((result, index) => (
                    <div key={index} className="bg-background/30 border border-border/60 rounded-lg p-4 hover:bg-background/50 transition-all duration-300">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="px-3 py-1 bg-primary/20 text-primary border border-primary/30 rounded-lg text-xs font-semibold">
                              #{result.citation_number}
                            </span>
                            <span className="px-2 py-1 bg-success/20 text-success border border-success/30 rounded text-xs font-medium">
                              {(result.score * 100).toFixed(1)}% match
                            </span>
                          </div>
                          <p className="text-foreground mb-3 leading-relaxed">{result.text}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {result.doc_id}
                            </span>
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              Chunk {result.chunk_idx}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : queryResults.length === 0 && !querying && queryText ? (
              <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex items-center justify-center">
                <div className="text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No results found. Try a different query.</p>
                </div>
              </div>
            ) : (
              <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex items-center justify-center">
                <div className="text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Enter a query to search the knowledge base</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Delete Document</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this document? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, docId: null })}
                className="flex-1 px-4 py-2 bg-background/30 border border-border/60 rounded-lg text-foreground hover:bg-background/50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-destructive text-primary-foreground rounded-lg hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20 transition-all duration-300"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All / namespace purge modal */}
      {purgeModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Delete All Documents</h3>
            </div>

            <p className="text-muted-foreground mb-4">
              This deletes every document in the selected knowledge base and clears its
              Pinecone namespace, so the agent stops answering from this content. This
              cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Bot className="w-4 h-4" />
                Knowledge base to clear <span className="text-destructive">*</span>
              </label>
              <AnimatedDropdown
                value={purgeModal.agentId}
                onChange={(value) => setPurgeModal({ ...purgeModal, agentId: value })}
                options={[
                  { value: '', label: 'Select a knowledge base' },
                  ...agentsList.map(agent => ({ value: agent.id, label: agent.name })),
                  { value: '__org__', label: 'Organization-wide documents (no agent)' }
                ]}
                placeholder="Select a knowledge base"
                icon={Bot}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Type <span className="font-mono text-destructive">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={purgeModal.confirmText}
                onChange={(e) => setPurgeModal({ ...purgeModal, confirmText: e.target.value })}
                placeholder="DELETE"
                autoComplete="off"
                className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-destructive/50 focus:border-destructive transition-all duration-300"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPurgeModal({ show: false, agentId: '', confirmText: '' })}
                disabled={purging}
                className="flex-1 px-4 py-2 bg-background/30 border border-border/60 rounded-lg text-foreground hover:bg-background/50 disabled:opacity-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurge}
                disabled={purging || purgeModal.confirmText !== 'DELETE' || !purgeModal.agentId}
                className="flex-1 px-4 py-2 bg-destructive text-primary-foreground rounded-lg hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
              >
                {purging ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseManager;
